import {
  Box,
  Flex,
  HStack,
  Kbd,
  NativeSelect,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Tag } from '@chakra-ui/react'
import { t } from 'i18next'
import { useAtom } from 'jotai'
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { ExperimentState, experimentStateAtom } from '~/atoms/ExperimentsState'
import {
  MangoButton,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import { useToastAlert } from '~/hooks/toast/useToastAlerts'
import useElectronIPC from '~/hooks/useElectronIPC'
import { Size } from '~/types/ThemeTypes'

import { Toggle } from '@mangovoice/mango-ui'

import {
  allowedLetters,
  initialToggleStates,
  keyCommands,
  parseKeyComboString,
} from './GlobalHotKeys.utils'

export enum GlobalHotKeyResult {
  Success = 'success',
  Error = 'error',
}

const GlobalHotKeyToggle = (props: {
  commandDisplay: string
  onToggle: (state: boolean) => void
  checked: boolean
}) => {
  const { commandDisplay, onToggle, checked } = props

  return (
    <Flex
      key={`toggle-${commandDisplay}`}
      alignItems="center"
      mr={4}
      mb={{ base: 2, md: 1 }}
    >
      <Toggle
        id={`hotkey-toggle-${commandDisplay}`}
        size={'md'}
        checked={checked}
        onCheckedChange={(details: { checked: boolean }) =>
          onToggle(details.checked)
        }
      >
        <Text mr={'.5rem'} fontSize="sm">
          {commandDisplay}
        </Text>
      </Toggle>
    </Flex>
  )
}

export const GlobalHotKeys = () => {
  const [experimentState, setExperimentState] = useAtom(experimentStateAtom)
  const { toastSuccess, toastError } = useToastAlert()

  const { initGetCopyNumberHotKeyResultListener } = useElectronIPC()

  const [toggleStates, setToggleStates] =
    useState<Record<string, boolean>>(initialToggleStates)
  const [associatedLetter, setAssociatedLetter] = useState<string>('')
  const [isInitialized, setIsInitialized] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  const isFeatureEnabledInJotai = useMemo(() => {
    return experimentState.globalPhoneCapture?.enabled ?? false
  }, [experimentState.globalPhoneCapture])

  const savedKeyComboInJotai = useMemo(() => {
    return experimentState.globalPhoneCapture?.keyCombo
  }, [experimentState.globalPhoneCapture])

  const currentUIConfigString = useMemo(() => {
    const enabledCommands = keyCommands
      .filter((cmd) => toggleStates[cmd.value])
      .map((cmd) => cmd.value)

    if (
      enabledCommands.length === 0 ||
      !associatedLetter ||
      associatedLetter.trim().length === 0
    ) {
      return undefined
    }

    return `${enabledCommands.join('+')}+${associatedLetter}`
  }, [toggleStates, associatedLetter, keyCommands])

  const handleSetConfigToState = useCallback(
    (type: GlobalHotKeyResult) => {
      if (type === GlobalHotKeyResult.Success) {
        setExperimentState((prevState: ExperimentState) => ({
          ...prevState,
          globalPhoneCapture: {
            ...prevState.globalPhoneCapture,
            enabled: true,
            keyCombo: currentUIConfigString,
          },
        }))
        toastSuccess(t('global.toast.globalPhoneHotKeySuccess'))
      } else {
        toastError(t('global.toast.globalPhoneHotKeyError'))
      }
    },
    [currentUIConfigString, setExperimentState, toastSuccess, toastError],
  )

  // Effect to initialize IPC listener
  useEffect(() => {
    const remove = initGetCopyNumberHotKeyResultListener(
      handleSetConfigToState,
      false,
    )

    return () => {
      remove()
    }
  }, [handleSetConfigToState])

  // Effect to load the UI state (toggles, letter) from savedKeyComboInJotai
  useEffect(() => {
    if (savedKeyComboInJotai) {
      const parsed = parseKeyComboString(savedKeyComboInJotai)
      if (parsed) {
        setToggleStates(parsed.newToggleStates)
        setAssociatedLetter(parsed.newLetter)
      } else {
        // Invalid saved combo string, reset UI to defaults
        setToggleStates(initialToggleStates)
        setAssociatedLetter('')
      }
    } else {
      // No keyCombo saved in Jotai, reset UI to defaults
      setToggleStates(initialToggleStates)
      setAssociatedLetter('')
    }
    // Set initialized flag after loading state
    if (!isInitialized) {
      setIsInitialized(true)
    }
  }, [savedKeyComboInJotai, isInitialized])

  const handleToggleChange = useCallback(
    (command: string, newState: boolean) => {
      setToggleStates((prevStates) => ({ ...prevStates, [command]: newState }))
    },
    [],
  )

  const handleLetterSelectChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      setAssociatedLetter(event.target.value)
    },
    [],
  )

  // Effect to calculate if there are unsaved changes
  useEffect(() => {
    if (!isInitialized) {
      return
    }

    setHasChanges(currentUIConfigString !== savedKeyComboInJotai)
  }, [isInitialized, currentUIConfigString, savedKeyComboInJotai])

  const handleSetOrUpdateHotkey = () => {
    if (currentUIConfigString) {
      window.electron?.hotKeys.sendSetGlobalPhoneHotkey(currentUIConfigString)
    }
  }

  const handleToggleHotkeyToggleEnabled = () => {
    const currentIsEnabled =
      experimentState.globalPhoneCapture?.enabled ?? false
    const newIsEnabled = !currentIsEnabled
    const existingKeyCombo = experimentState.globalPhoneCapture?.keyCombo

    setExperimentState((prevState: ExperimentState) => ({
      ...prevState,
      globalPhoneCapture: {
        ...prevState.globalPhoneCapture,
        enabled: newIsEnabled,
      },
    }))

    if (newIsEnabled && existingKeyCombo) {
      window.electron?.hotKeys.sendSetGlobalPhoneHotkey(existingKeyCombo)
      // No toast needed
    } else if (!newIsEnabled && existingKeyCombo) {
      window.electron?.hotKeys?.sendRemoveGlobalPhoneHotkey(existingKeyCombo)
      toastSuccess(t('global.toast.globalPhoneHotKeyDisabled'))
    }
  }

  const handleClearHotkey = () => {
    setExperimentState((prevState: ExperimentState) => ({
      ...prevState,
      globalPhoneCapture: {
        enabled: false,
        keyCombo: undefined,
      },
    }))

    if (savedKeyComboInJotai) {
      window.electron?.hotKeys?.sendRemoveGlobalPhoneHotkey(
        savedKeyComboInJotai,
      )
    }

    setToggleStates(initialToggleStates)
    setAssociatedLetter('')
    toastSuccess(t('experimentsState.globalPhoneCapture.toast.hotkeyCleared'))
  }

  const formatHotKeyForDisplay = (
    hotkeyString: string | undefined,
    variant: 'active' | 'change',
  ): ReactNode => {
    if (!hotkeyString) {
      return (
        <Text as="em">{t('experimentsState.globalPhoneCapture.notSet')}</Text>
      )
    }

    // If the string doesn't contain '+', it might be a single key (though our UI forms combos)
    // or an invalid/incomplete string. For now, treat it as a single part.
    if (!hotkeyString.includes('+')) {
      const commandConfig = keyCommands.find((kc) => kc.value === hotkeyString)
      const displayPart = commandConfig ? commandConfig.display : hotkeyString
      return (
        <Kbd
          backgroundColor={variant === 'active' ? 'green.700' : 'orange.400'}
          borderColor={variant === 'active' ? 'green.200' : 'orange.200'}
        >
          {displayPart}
        </Kbd>
      )
    }

    const parts = hotkeyString.split('+')

    return parts.map((part, index) => {
      const commandConfig = keyCommands.find((kc) => kc.value === part)
      const displayPart = commandConfig ? commandConfig.display : part

      return (
        <React.Fragment key={`display-${part}-${index}`}>
          <Kbd
            backgroundColor={variant === 'active' ? 'green.700' : 'orange.400'}
            borderColor={variant === 'active' ? 'green.200' : 'orange.200'}
          >
            {displayPart}
          </Kbd>
          {index === parts.length - 1 ? '' : ' + '}
        </React.Fragment>
      )
    })
  }

  let isMainButtonDisabled: boolean
  const isUIConfigIncomplete = !currentUIConfigString

  if (!isFeatureEnabledInJotai && hasChanges) {
    // Can only enable if the current UI configuration is complete
    isMainButtonDisabled = isUIConfigIncomplete
  } else {
    // Feature is currently enabled
    if (hasChanges) {
      // Can only update if the current UI configuration is complete
      isMainButtonDisabled = isUIConfigIncomplete
    } else {
      // No changes to save, and it's already set
      isMainButtonDisabled = true
    }
  }

  return (
    <VStack gap={4} align="stretch" borderWidth="1px" borderRadius="md" p={4}>
      <Box alignItems={'center'}>
        <Flex alignItems="center" gap={2} mb={1} flexWrap="wrap">
          <Text fontWeight="bold">
            {t('experimentsState.globalPhoneCapture.activeSettingTitle')}
          </Text>
          {isFeatureEnabledInJotai ? (
            <Tag.Root
              size={'md'}
              key={'feature-enabled'}
              borderRadius="full"
              variant="solid"
              backgroundColor={'green.700'}
            >
              {t('experimentsState.globalPhoneCapture.status.enabled')}
            </Tag.Root>
          ) : (
            <Tag.Root
              size={'md'}
              key={'feature-disabled'}
              borderRadius="full"
              variant="solid"
              backgroundColor={'red.700'}
            >
              {t('experimentsState.globalPhoneCapture.status.disabled')}
            </Tag.Root>
          )}
        </Flex>
        {savedKeyComboInJotai && (
          <Text color="fg.success" mt={4} key={'saved-key-combo'}>
            {t('experimentsState.globalPhoneCapture.activeAndSet')}:{' '}
            {formatHotKeyForDisplay(savedKeyComboInJotai, 'active')}
          </Text>
        )}
        {isInitialized && hasChanges && !isUIConfigIncomplete && (
          <Text color="fg.warning" mt={3} key={'proposed-key-combo'}>
            {t('experimentsState.globalPhoneCapture.unsavedChanges')}{' '}
            {formatHotKeyForDisplay(currentUIConfigString, 'change')}
          </Text>
        )}
      </Box>
      <hr />
      <Box>
        <Text fontWeight="bold" mb={2}>
          {t('experimentsState.globalPhoneCapture.configureTitle')}
        </Text>
        <Flex alignItems={'flex-start'} flexWrap="wrap">
          {keyCommands.map((command) => (
            <GlobalHotKeyToggle
              key={`toggle-parent-${command.value}`}
              commandDisplay={command.display}
              checked={toggleStates[command.value] ?? false}
              onToggle={(newState) =>
                handleToggleChange(command.value, newState)
              }
            />
          ))}
        </Flex>
        <Flex alignItems={'flex-start'} flexWrap="wrap">
          <Flex mt={{ base: 2, md: 4 }} alignItems={'center'}>
            <Text mr={'.5rem'} fontWeight={'bold'} fontSize="sm">
              {t('experimentsState.globalPhoneCapture.keyLabel')}
            </Text>
            <NativeSelect.Root size="sm" width="110px">
              <NativeSelect.Field
                placeholder={'-'}
                value={associatedLetter}
                onChange={handleLetterSelectChange}
              >
                {allowedLetters.map((letter) => (
                  <option key={`option-${letter}`} value={letter}>
                    {letter}
                  </option>
                ))}
              </NativeSelect.Field>
            </NativeSelect.Root>
          </Flex>
        </Flex>
      </Box>

      <HStack
        justifyContent={'flex-start'}
        gap={3}
        mt={3}
        mb={0}
        flexWrap="wrap"
      >
        <MangoButton
          variant={MangoButtonVariant.Success_300}
          size={Size.sm}
          disabled={isMainButtonDisabled}
          onClick={handleSetOrUpdateHotkey}
        >
          {t('experimentsState.globalPhoneCapture.button.setHotkey')}
        </MangoButton>
        <MangoButton
          variant={
            isFeatureEnabledInJotai
              ? MangoButtonVariant.Destructive_300
              : MangoButtonVariant.Success_300
          }
          size={Size.sm}
          disabled={!savedKeyComboInJotai}
          onClick={handleToggleHotkeyToggleEnabled}
        >
          {isFeatureEnabledInJotai
            ? t('experimentsState.globalPhoneCapture.button.disableFeature')
            : t('experimentsState.globalPhoneCapture.button.enableFeature')}
        </MangoButton>
        <MangoButton
          variant={MangoButtonVariant.Destructive_300}
          size={Size.sm}
          disabled={!savedKeyComboInJotai}
          onClick={handleClearHotkey}
        >
          {t('experimentsState.globalPhoneCapture.button.clearHotkey')}
        </MangoButton>
      </HStack>
    </VStack>
  )
}
